import { useEffect, useRef, useState } from "react";
import { useSolanaMoney } from "../hooks/useSolanaMoney";
import { formatSol, LAMPORTS_PER_SOL, WITHDRAW_FEE_LAMPORTS } from "../contracts/solana/config";
import { useSolUsdPrice, formatUsd } from "../hooks/useSolUsdPrice";
import { useProfile } from "../hooks/useProfile";
import type { AuthUser } from "../hooks/useAuth";
import SolanaWithdrawModal from "./SolanaWithdrawModal";

/* ============================================================
   Solana wallet panel — the SOLANA_NETWORK counterpart to
   WalletPanel. Same draggable "Wallet Card" chrome and warm
   `wc-` palette. Balance is Vault `available` + wallet SOL;
   Deposit just copies the wallet address (Quick Play's
   auto-sweep moves wallet SOL into the Vault, so there is no
   deposit screen) and Withdraw runs a real on-chain tx.

   Kept deliberately slimmer than the EVM panel: balance,
   Deposit, Withdraw, Receive (copy address), Sign out. No
   legacy vault, USDC allowance, or Quick Play sections.
   ============================================================ */

interface SolanaWalletPanelProps {
  walletAddress: string | null;
  user: AuthUser | null;
  onClose: () => void;
  onSignOut: () => void;
  initialView?: "wallet" | "deposit" | "withdraw" | "send";
}

const PANEL_WIDTH = 380;
const PANEL_DEFAULT_TOP = 80;
const PANEL_RIGHT_MARGIN = 24;

function defaultPosition(): { x: number; y: number } {
  if (typeof window === "undefined") return { x: 0, y: PANEL_DEFAULT_TOP };
  return {
    x: Math.max(16, window.innerWidth - PANEL_WIDTH - PANEL_RIGHT_MARGIN),
    y: PANEL_DEFAULT_TOP,
  };
}

// ── Inline icons (match WalletPanel's handoff SVGs) ──────────────────
const CloseIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
);
const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" /><path d="M5 15V5a2 2 0 012-2h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
);
const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="#5fcf80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const PlusIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" /></svg>
);
const ReceiveIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const WithdrawIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const SignOutIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const InfoIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" /><path d="M12 11v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><circle cx="12" cy="7.5" r="1.3" fill="currentColor" /></svg>
);
const RefreshIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M23 4v6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

// Reuse the EVM panel's warm `wc-` palette so the two panels look identical.
const WC_STYLES = `
.wc-card {
  --bg:#0a0806; --card:#0e0c0a; --card-2:#14110e; --line:#211c16; --line-2:#2d251c;
  --text:#f5efe6; --muted:#8a7f72; --muted-2:#5a5247; --accent:#ff5722; --accent-2:#ff8a4a;
  --green:#5fcf80; --green-line:rgba(95,207,128,0.30);
  border-radius: 18px;
  background:
    radial-gradient(ellipse 80% 40% at 50% 0%, rgba(255,87,34,0.06), transparent 70%),
    linear-gradient(180deg, rgba(20,17,14,0.95), rgba(10,8,6,0.98));
  box-shadow: 0 40px 100px -30px rgba(0,0,0,0.6), 0 0 0 1px var(--line-2), inset 0 1px 0 rgba(255,138,74,0.06);
  position: relative; overflow: hidden; backdrop-filter: blur(12px);
  color: var(--text); font-family: 'Geist', -apple-system, BlinkMacSystemFont, sans-serif;
  display: flex; flex-direction: column;
}
.wc-card::before { content:''; position:absolute; top:0; left:0; right:0; height:2px;
  background: linear-gradient(90deg, transparent 0%, var(--accent) 30%, var(--accent-2) 50%, var(--accent) 70%, transparent 100%);
  opacity:0.85; box-shadow:0 0 12px rgba(255,87,34,0.4); }
.wc-card::after { content:''; position:absolute; top:0; left:0; right:0; height:2px; width:80px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent);
  animation: wc-sweep 4.5s linear infinite; pointer-events:none; }
.wc-header { display:flex; align-items:center; justify-content:space-between; padding:18px 20px; border-bottom:1px solid var(--line); }
.wc-header-left { display:flex; align-items:center; gap:12px; }
.wc-grip { display:grid; grid-template-columns:repeat(2,3px); grid-template-rows:repeat(3,3px); gap:3px; opacity:0.45; transition:opacity 160ms ease; }
.wc-header-left:hover .wc-grip { opacity:0.8; }
.wc-grip span { width:3px; height:3px; border-radius:999px; background:var(--muted); }
.wc-header-id { display:flex; flex-direction:column; line-height:1; min-width:0; }
.wc-label { font-family:'Geist Mono',ui-monospace,monospace; font-size:10px; letter-spacing:1.8px; color:var(--muted-2); font-weight:600; margin-bottom:5px; }
.wc-handle { font-family:'Geist Mono',ui-monospace,monospace; font-size:13.5px; color:var(--text); font-weight:600; letter-spacing:0.1px; max-width:230px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.wc-close { width:30px; height:30px; border-radius:999px; border:1px solid var(--line-2); background:transparent; color:var(--muted); display:inline-flex; align-items:center; justify-content:center; cursor:pointer; transition:all 160ms ease; flex-shrink:0; }
.wc-close:hover { color:var(--text); border-color:var(--muted); background:rgba(255,255,255,0.03); }
.wc-body { padding:18px 20px 20px; display:flex; flex-direction:column; gap:14px; flex:1 1 auto; min-height:0; overflow-y:auto; }
.wc-body > * { flex-shrink:0; }
.wc-balance { position:relative; border-radius:14px; padding:22px 22px 20px; overflow:hidden;
  background: radial-gradient(ellipse 80% 60% at 100% 0%, rgba(255,87,34,0.11), transparent 70%), linear-gradient(180deg, rgba(28,22,18,0.62), rgba(14,11,9,0.72));
  border:1px solid rgba(255,87,34,0.23); }
.wc-corner-line { position:absolute; top:0; left:14px; right:14px; height:1px; background:linear-gradient(90deg, transparent, rgba(255,138,74,0.5), transparent); }
.wc-balance-label-row { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; }
.wc-balance-label { font-family:'Geist Mono',ui-monospace,monospace; font-size:10px; letter-spacing:1.8px; color:var(--muted); font-weight:600; }
.wc-balance-value { display:flex; align-items:baseline; gap:6px; margin-bottom:8px; }
.wc-amount { font-family:'Geist Mono',ui-monospace,monospace; font-size:60px; font-weight:700; color:var(--text); letter-spacing:-2.2px; line-height:1.1; padding-bottom:2px;
  font-variant-numeric:tabular-nums; background:linear-gradient(180deg, #fff8ef 0%, #f5efe6 70%, #d8cdb5 100%); background-clip:text; -webkit-background-clip:text; -webkit-text-fill-color:transparent; }
.wc-cur { font-family:'Geist Mono',ui-monospace,monospace; font-size:13px; color:var(--muted); margin-left:4px; letter-spacing:0.6px; transform:translateY(-22px); font-weight:500; }
.wc-balance-sub { font-size:11.5px; color:var(--muted); line-height:1.4; }
.wc-balance-sub b { color:var(--text); font-weight:500; }
.wc-balance-split { border-top:1px solid rgba(255,138,74,0.16); margin-top:14px; padding-top:10px; display:flex; flex-direction:column; gap:4px; }
.wc-balance-row { display:flex; align-items:center; justify-content:space-between; font-size:12px; }
.wc-balance-row-label { display:inline-flex; align-items:center; gap:5px; color:var(--muted); }
.wc-balance-row-value { font-family:'Geist Mono',ui-monospace,monospace; color:var(--text); font-weight:500; font-variant-numeric:tabular-nums; }
.wc-balance-row-value.dim { color:var(--muted); }
.wc-info-btn { appearance:none; border:none; background:transparent; padding:2px; margin:0; color:var(--muted-2); cursor:pointer; display:inline-flex; align-items:center; justify-content:center; border-radius:4px; transition:color 160ms ease; }
.wc-info-btn:hover, .wc-info-btn[aria-expanded="true"] { color:var(--text); }
.wc-reserve-note { border-radius:8px; border:1px solid var(--line-2); background:rgba(255,255,255,0.02); padding:9px 11px; margin-top:6px; font-size:11px; color:var(--muted); line-height:1.5; }
.wc-deposit-hint { font-size:11px; color:var(--muted-2); text-align:center; line-height:1.45; margin-top:-6px; }
.wc-refresh-btn { width:26px; height:26px; border-radius:999px; border:1px solid var(--line-2); background:transparent; color:var(--muted); display:inline-flex; align-items:center; justify-content:center; cursor:pointer; transition:all 160ms ease; flex-shrink:0; }
.wc-refresh-btn:hover { color:var(--text); border-color:var(--muted); background:rgba(255,255,255,0.03); }
.wc-refresh-btn:disabled { cursor:default; }
.wc-refresh-btn.spinning svg { animation:wc-spin 0.8s linear infinite; }
@keyframes wc-spin { to { transform:rotate(360deg); } }
.wc-sect-label { font-family:'Geist Mono',ui-monospace,monospace; font-size:10px; letter-spacing:1.8px; color:var(--muted-2); font-weight:600; margin-top:4px; margin-bottom:-4px; }
.wc-address-row { display:flex; align-items:center; border-radius:10px; border:1px solid var(--line); background:rgba(255,255,255,0.014); padding:0 4px 0 12px; height:40px; transition:border-color 160ms ease; }
.wc-address-row:hover { border-color:var(--line-2); }
.wc-address-value { flex:1; min-width:0; font-family:'Geist Mono',ui-monospace,monospace; font-size:12px; color:var(--text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; letter-spacing:0.1px; }
.wc-icon-btn { width:30px; height:30px; border-radius:6px; border:none; background:transparent; color:var(--muted); cursor:pointer; display:inline-flex; align-items:center; justify-content:center; transition:all 160ms ease; flex-shrink:0; text-decoration:none; }
.wc-icon-btn:hover { color:var(--text); background:rgba(255,255,255,0.04); }
.wc-icon-btn:disabled { opacity:0.35; cursor:default; }
.wc-network-row { display:flex; align-items:center; justify-content:space-between; padding:0 14px; border-radius:10px; border:1px solid var(--line); background:rgba(255,255,255,0.014); height:40px; }
.wc-network-name { display:inline-flex; align-items:center; gap:9px; font-size:12.5px; color:var(--text); font-weight:500; }
.wc-network-dot { width:7px; height:7px; border-radius:999px; background:var(--green); box-shadow:0 0 0 2px rgba(95,207,128,0.18), 0 0 8px var(--green); }
.wc-network-logo { width:16px; height:auto; object-fit:contain; display:block; }
.wc-network-mode { font-family:'Geist Mono',ui-monospace,monospace; font-size:9.5px; letter-spacing:1.4px; color:var(--muted-2); font-weight:600; padding:3px 7px; border-radius:4px; background:rgba(138,127,114,0.08); border:1px solid var(--line); }
.wc-btn-primary { appearance:none; border:none; border-radius:12px; cursor:pointer; height:50px;
  background:linear-gradient(180deg, #ff7b3d 0%, #ff5722 55%, #e94a18 100%); color:#fff; font-family:inherit; font-size:14px; font-weight:600; letter-spacing:0.1px;
  display:flex; align-items:center; justify-content:center; gap:8px; position:relative; overflow:hidden;
  box-shadow:0 10px 30px -14px rgba(255,87,34,0.6), 0 0 0 1px rgba(255,140,80,0.25) inset, 0 1px 0 rgba(255,255,255,0.15) inset; transition:all 180ms ease; }
.wc-btn-primary::before { content:''; position:absolute; top:0; left:0; right:0; height:50%; background:linear-gradient(180deg, rgba(255,255,255,0.18), transparent); pointer-events:none; }
.wc-btn-primary:hover { transform:translateY(-1px); box-shadow:0 18px 50px -18px rgba(255,87,34,0.7), 0 0 0 1px rgba(255,140,80,0.4) inset, 0 1px 0 rgba(255,255,255,0.15) inset; }
.wc-btn-primary:disabled { opacity:0.4; cursor:default; transform:none; }
.wc-actions { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-top:6px; }
.wc-btn-action { appearance:none; border:1px solid var(--line-2); border-radius:12px; cursor:pointer; height:60px; background:rgba(255,255,255,0.012); color:var(--text); font-family:inherit; font-size:13px; font-weight:500; letter-spacing:0.1px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:5px; transition:all 180ms ease; }
.wc-btn-action:not(:disabled):hover { border-color:var(--accent); background:rgba(255,87,34,0.06); transform:translateY(-1px); }
.wc-btn-action:not(:disabled):hover .wc-btn-icon { color:var(--accent); background:rgba(255,87,34,0.12); border-color:rgba(255,87,34,0.3); }
.wc-btn-action:disabled { opacity:0.45; cursor:default; }
.wc-btn-icon { width:22px; height:22px; border-radius:6px; background:rgba(255,255,255,0.03); border:1px solid var(--line); color:var(--muted); display:inline-flex; align-items:center; justify-content:center; transition:all 180ms ease; }
.wc-signout { background:transparent; border:none; color:var(--muted); cursor:pointer; padding:14px; margin:2px auto 0; font-family:'Geist Mono',ui-monospace,monospace; font-size:11.5px; font-weight:500; letter-spacing:0.8px; display:flex; align-items:center; justify-content:center; gap:8px; transition:color 160ms ease; }
.wc-signout:hover { color:var(--text); }
@keyframes wc-sweep { 0% { transform:translateX(-100%); } 100% { transform:translateX(100%); } }
@media (prefers-reduced-motion: reduce) { .wc-card::after { animation:none; } }
`;

export default function SolanaWalletPanel({
  walletAddress,
  user,
  onClose,
  onSignOut,
  initialView = "wallet",
}: SolanaWalletPanelProps) {
  const [copied, setCopied] = useState(false);
  const [showReserveInfo, setShowReserveInfo] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // "deposit" is not a view here anymore — Deposit copies the address in
  // place — so a leftover initialView="deposit" just opens the wallet.
  const [view, setView] = useState<"wallet" | "withdraw" | "send">(
    initialView === "deposit" ? "wallet" : initialView,
  );
  const [pos, setPos] = useState(defaultPosition);
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // The on-chain Vault balance (lamports) + the withdraw action. Deposit is
  // just "send SOL to your address" now — the Quick Play sweep does the rest.
  const { available, walletBalance, busy, withdraw, refresh } = useSolanaMoney(walletAddress);
  // (Quick Play delegation + auto-sweep now runs App-scoped via
  // SolanaQuickPlayDaemon, so it isn't mounted here — a single sweeper only.)
  // Live SOL/USD price for the headline USD figure (SOL amount shown beneath it).
  const { price: solPrice } = useSolUsdPrice();
  // The user's *custom* handle (backend profile) is the source of truth for the
  // name — falls back to the Privy display name only when it isn't set yet.
  const { profile } = useProfile();

  // Esc closes the panel — but only when no sub-modal is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && view === "wallet") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, view]);

  // Drag listeners (attached only while dragging).
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffset.current.x;
      const newY = e.clientY - dragOffset.current.y;
      const maxX = window.innerWidth - 80;
      const maxY = window.innerHeight - 60;
      setPos({
        x: Math.max(-PANEL_WIDTH + 80, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging]);

  const startDrag = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("[data-no-drag]")) return;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    setDragging(true);
    e.preventDefault();
  };

  const copy = async () => {
    if (!walletAddress) return;
    try {
      await navigator.clipboard.writeText(walletAddress);
    } catch {
      /* clipboard blocked */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Manual balance refresh — the same on-chain re-read the 15s poll does, on
  // demand. The spin lasts at least 500ms so a fast RPC round-trip still reads
  // as "it did something"; `refreshing` gates re-entry so impatient clicks
  // can't stack RPC calls.
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await Promise.all([refresh(), new Promise((r) => setTimeout(r, 500))]);
    } finally {
      setRefreshing(false);
    }
  };

  const handle = profile?.handle ?? user?.displayName ?? "Embedded wallet";
  // Headline balance = Vault `available` + un-deposited wallet SOL, matching the
  // header so it never reads 0 right after a deposit.
  const totalLamports = available === null ? null : available + (walletBalance ?? 0n);
  // Big figure = USD (like the header); the SOL amount goes in the subtitle.
  const sol = totalLamports === null ? null : Number(totalLamports) / LAMPORTS_PER_SOL;
  const usdText = sol == null || solPrice == null ? "…" : formatUsd(sol * solPrice);
  const solText = totalLamports === null ? "…" : `${formatSol(totalLamports)} SOL`;
  // Withdraw pays from the Vault AND un-swept wallet SOL (quick play must not
  // be required to cash out); the wallet side keeps the tx-fee reserve.
  const walletSpendable = (walletBalance ?? 0n) > WITHDRAW_FEE_LAMPORTS ? (walletBalance ?? 0n) - WITHDRAW_FEE_LAMPORTS : 0n;
  const canWithdraw = (available ?? 0n) + walletSpendable > 0n;
  // Balance breakdown: only the Vault side is stakeable — the wallet slice is
  // the sweep's network-fee reserve (withdrawable, just not playable). Rendered
  // only when it exists, so the headline never silently disagrees with the
  // stake screen. Rows fall back to SOL until the USD price loads.
  const rowAmount = (lamports: bigint): string =>
    solPrice == null ? `${formatSol(lamports)} SOL` : formatUsd((Number(lamports) / LAMPORTS_PER_SOL) * solPrice);
  const hasReserve = available !== null && (walletBalance ?? 0n) > 0n;

  return (
    <>
      <style>{WC_STYLES}</style>
      <div
        role="dialog"
        aria-label="Wallet"
        className="wc-card"
        style={{
          position: "fixed",
          zIndex: 50,
          left: pos.x,
          top: pos.y,
          width: "min(380px, calc(100vw - 24px))",
          maxHeight: "calc(100vh - 96px)",
          userSelect: dragging ? "none" : "auto",
        }}
      >
        {/* Header (drag handle) */}
        <div className="wc-header" onMouseDown={startDrag} style={{ cursor: dragging ? "grabbing" : "grab" }}>
          <div className="wc-header-left">
            <span className="wc-grip" aria-hidden="true">
              <span /><span /><span /><span /><span /><span />
            </span>
            <div className="wc-header-id">
              <span className="wc-label">WALLET</span>
              <span className="wc-handle" title={handle}>{handle}</span>
            </div>
          </div>
          <button data-no-drag onClick={onClose} aria-label="Close wallet panel" className="wc-close">
            <CloseIcon />
          </button>
        </div>

        <div className="wc-body">
          {/* Balance — full balance (Vault available + wallet SOL) in USD, with
              the SOL amount beneath. Withdraw acts on this same total. */}
          <div className="wc-balance">
            <div className="wc-corner-line" />
            <div className="wc-balance-label-row">
              <span className="wc-balance-label">BALANCE</span>
              <button
                data-no-drag
                className={`wc-refresh-btn${refreshing ? " spinning" : ""}`}
                onClick={handleRefresh}
                disabled={refreshing || !walletAddress}
                aria-label="Refresh balance"
                title="Refresh balance"
              >
                <RefreshIcon />
              </button>
            </div>
            <div className="wc-balance-value">
              <span className="wc-amount">{usdText}</span>
            </div>
            <div className="wc-balance-sub"><b>{solText}</b></div>
            {hasReserve && (
              <div className="wc-balance-split">
                <div className="wc-balance-row">
                  <span className="wc-balance-row-label">Playable</span>
                  <span className="wc-balance-row-value">{rowAmount(available ?? 0n)}</span>
                </div>
                <div className="wc-balance-row">
                  <span className="wc-balance-row-label">
                    Network fee reserve
                    <button
                      data-no-drag
                      className="wc-info-btn"
                      aria-label="About the network fee reserve"
                      aria-expanded={showReserveInfo}
                      onClick={() => setShowReserveInfo((v) => !v)}
                    >
                      <InfoIcon />
                    </button>
                  </span>
                  <span className="wc-balance-row-value dim">{rowAmount(walletBalance ?? 0n)}</span>
                </div>
                {showReserveInfo && (
                  <div className="wc-reserve-note">
                    Set aside so Solana network fees never block a deposit or withdrawal. You can
                    withdraw it anytime — it just can't be staked.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Address */}
          <div className="wc-sect-label">ADDRESS</div>
          <div className="wc-address-row">
            <span className="wc-address-value" title={walletAddress ?? undefined}>{walletAddress ?? "—"}</span>
            <button data-no-drag onClick={copy} disabled={!walletAddress} aria-label="Copy address" title="Copy" className="wc-icon-btn">
              {copied ? <CheckIcon /> : <CopyIcon />}
            </button>
          </div>

          {/* Network */}
          <div className="wc-network-row">
            <span className="wc-network-name">
              <img src="/solana-mark.png" alt="" className="wc-network-logo" aria-hidden="true" />
              Solana network
            </span>
          </div>

          {/* Actions */}
          <button data-no-drag className="wc-btn-primary" onClick={copy} disabled={!walletAddress} title="Copy your address to receive SOL">
            <PlusIcon /> {copied ? "Copied ✓" : "Deposit"}
          </button>
          <div className="wc-deposit-hint">
            First deposit: add ~$0.20 for one-time network fees. Later deposits don't need it.
          </div>
          <div className="wc-actions">
            <button data-no-drag className="wc-btn-action" onClick={() => setView("withdraw")} disabled={!canWithdraw} title={!canWithdraw ? "Nothing to withdraw" : "Withdraw SOL to an external wallet"}>
              <span className="wc-btn-icon"><WithdrawIcon /></span>
              Withdraw
            </button>
            <button data-no-drag className="wc-btn-action" onClick={copy} disabled={!walletAddress} title="Copy address to receive SOL">
              <span className="wc-btn-icon"><ReceiveIcon /></span>
              {copied ? "Copied ✓" : "Receive"}
            </button>
          </div>

          <button data-no-drag className="wc-signout" onClick={() => { onSignOut(); onClose(); }}>
            <SignOutIcon /> Sign out
          </button>
        </div>
      </div>

      {view === "withdraw" && (
        <SolanaWithdrawModal
          walletAddress={walletAddress}
          available={available}
          walletBalance={walletBalance}
          busy={busy}
          withdraw={withdraw}
          onClose={() => setView("wallet")}
        />
      )}
    </>
  );
}
